/**
 * DashboardDateRangeSelector — header date control for the home dashboard.
 *
 * Layout: 4 quick-range pills (Today / This Week / This Month / Last Month)
 * + a Custom pill that opens an inline two-date react-datepicker. Picking
 * the custom range writes `{ key: "custom", fromDate, toDate }` into
 * RangeContext; widget hooks pick the change up via their cache keys.
 */

import * as React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./DateRangeSelector.css";
import {
  DashboardRangeKey,
  DASHBOARD_RANGE_LABELS,
  useDashboardRangeControl,
} from "./RangeContext";

const QUICK_KEYS: ReadonlyArray<Exclude<DashboardRangeKey, "custom">> = [
  "today",
  "this_week",
  "this_month",
  "last_month",
];

export function DashboardDateRangeSelector() {
  const { range, setRange } = useDashboardRangeControl();
  const isCustom = range.key === "custom";
  // On phones the inline popper runs off-screen; render the calendar as a
  // centered portal overlay instead.
  const isMobile = useIsMobile();

  const committedStart = parseYyyyMmDd(range.fromDate);
  const committedEnd = parseYyyyMmDd(range.toDate);

  // In-progress selection state — kept local so we don't update context
  // (and trigger a customInput re-render that closes the popper) between
  // clicks. We only commit to context once BOTH dates are picked.
  const [pendingStart, setPendingStart] = React.useState<Date | null>(null);
  // Ref to imperatively close the calendar once the range commits.
  const pickerRef = React.useRef<DatePicker | null>(null);

  // What the calendar should show as currently-selected range. Pending
  // state takes priority: once user starts a new selection, ignore the
  // previously committed range so the calendar reflects the new attempt.
  const displayStart = pendingStart ?? committedStart;
  const displayEnd = pendingStart ? null : committedEnd;

  const customLabel =
    isCustom && committedStart && committedEnd
      ? `${formatShort(committedStart)} – ${formatShort(committedEnd)}`
      : "Custom";

  const handleCustomChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    if (start && end) {
      // Both picked — commit, clear pending, and close the picker so
      // the user lands back on the dashboard with the range applied.
      // Same date twice is valid (single-day range).
      setRange({
        key: "custom",
        fromDate: formatYyyyMmDd(start),
        toDate: formatYyyyMmDd(end),
      });
      setPendingStart(null);
      pickerRef.current?.setOpen(false);
    } else if (start) {
      // First click — local-only so the picker stays open.
      setPendingStart(start);
    } else {
      // User cleared selection.
      setPendingStart(null);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1">
      {QUICK_KEYS.map((key) => (
        <PillButton
          key={key}
          selected={range.key === key}
          onClick={() => {
            setRange({ key });
            setPendingStart(null);
          }}
        >
          {DASHBOARD_RANGE_LABELS[key]}
        </PillButton>
      ))}
      <DatePicker
        ref={pickerRef}
        selectsRange
        startDate={displayStart ?? undefined}
        endDate={displayEnd ?? undefined}
        // Reopen on the first day of the selected range (or the in-progress
        // pick) instead of always jumping to today's month.
        openToDate={displayStart ?? undefined}
        calendarClassName="dashboard-datepicker"
        onChange={handleCustomChange}
        // Keep open after the first click. react-datepicker auto-closes
        // once both dates are picked AND onChange has committed; we also
        // close-on-blur naturally because the customInput is a button.
        shouldCloseOnSelect={false}
        // Reset partial selection if user clicks outside without finishing.
        onCalendarClose={() => setPendingStart(null)}
        // No maxDate: future dates are allowed so aging can forecast "what
        // will this look like on date X" (and date-span widgets can look ahead).
        dateFormat="MM/dd/yyyy"
        // Mobile: centered modal overlay so the whole calendar fits the
        // viewport. Desktop keeps the inline popper anchored to the pill.
        withPortal={isMobile}
        popperPlacement="bottom-end"
        customInput={<PillButton selected={isCustom}>{customLabel}</PillButton>}
      />
    </div>
  );
}

/**
 * Pill that matches the existing dashboard chip look — small, rounded,
 * subtle background, blue when selected.
 *
 * `forwardRef` is required so react-datepicker can wire its `customInput`
 * to a focusable trigger.
 */
type PillButtonProps = {
  children: React.ReactNode;
  selected?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

const PillButton = React.forwardRef<HTMLButtonElement, PillButtonProps>(
  function PillButton({ children, selected, onClick }, ref) {
    const base =
      "px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap";
    const tone = selected
      ? "bg-blue-600 text-white shadow-sm"
      : "bg-gray-100 text-gray-700 hover:bg-gray-200";
    return (
      <button
        ref={ref}
        type="button"
        className={`${base} ${tone}`}
        onClick={onClick}
      >
        {children}
      </button>
    );
  },
);

/**
 * True when the viewport is at or below Tailwind's `sm` breakpoint (640px).
 * Used to switch the date picker to a centered portal overlay on phones.
 */
function useIsMobile(query = "(max-width: 640px)"): boolean {
  const getMatch = () =>
    typeof window !== "undefined" && window.matchMedia(query).matches;
  const [isMobile, setIsMobile] = React.useState<boolean>(getMatch);

  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    // addEventListener is the modern API; addListener is the Safari < 14 fallback.
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    setIsMobile(mql.matches);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);

  return isMobile;
}

function parseYyyyMmDd(s: string | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatYyyyMmDd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatShort(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${m}/${day}`;
}
